import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import PrivacyPolicyModal from './PrivacyPolicyModal';
import TermsOfServiceModal from './TermsOfServiceModal';

interface ConsentPreferences {
  analytics: boolean;
  ads: boolean;
  personalization: boolean;
  necessary: boolean; // Always true, but included for clarity
}

interface ConsentBannerProps {
  onConsentChange: (preferences: ConsentPreferences) => void;
  isVisible: boolean;
}

const ConsentBanner: React.FC<ConsentBannerProps> = ({ onConsentChange, isVisible }) => {
  const [preferences, setPreferences] = useState<ConsentPreferences>({
    analytics: false,
    ads: false,
    personalization: false,
    necessary: true,
  });
  const [showDetails, setShowDetails] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);

  console.log('[ConsentBanner] Rendering with isVisible:', isVisible);

  // Prevent body scroll when modal is visible
  useEffect(() => {
    if (isVisible) {
      document.body.style.overflow = 'hidden';
      console.log('[ConsentBanner] Modal visible, preventing body scroll');
    } else {
      document.body.style.overflow = 'unset';
      console.log('[ConsentBanner] Modal hidden, restoring body scroll');
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isVisible]);

  const handleAcceptAll = () => {
    const allAccepted = {
      analytics: true,
      ads: true,
      personalization: true,
      necessary: true,
    };
    setPreferences(allAccepted);
    onConsentChange(allAccepted);
  };

  const handleAcceptNecessary = () => {
    const necessaryOnly = {
      analytics: false,
      ads: false,
      personalization: false,
      necessary: true,
    };
    setPreferences(necessaryOnly);
    onConsentChange(necessaryOnly);
  };

  const handleSavePreferences = () => {
    onConsentChange(preferences);
  };

  const handleToggle = (key: keyof ConsentPreferences) => {
    if (key === 'necessary') return; // Necessary cookies cannot be disabled
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  return (
    <>
      {createPortal(
        <AnimatePresence>
          {isVisible && (
            <>
              {/* Backdrop */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  zIndex: 9999,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              />
              
              {/* Modal */}
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 10000,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '1rem'
                }}
              >
                <div style={{
                  backgroundColor: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '0.5rem',
                  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                  padding: '1.5rem',
                  maxWidth: '32rem',
                  width: '100%',
                  maxHeight: '90vh',
                  overflowY: 'auto'
                }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {/* Header */}
                    <div style={{ textAlign: 'center' }}>
                      <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#111827', marginBottom: '0.5rem' }}>
                        We value your privacy
                      </h3>
                      <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                        We use cookies and similar technologies to provide, protect, and improve our services. 
                        By clicking "Accept All", you consent to our use of cookies for analytics and personalized ads. 
                        You can customize your preferences below.
                      </p>
                    </div>
                    
                    {/* Quick Actions */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', justifyContent: 'center' }}>
                      <button
                        onClick={handleAcceptAll}
                        style={{
                          padding: '0.75rem 1.5rem',
                          backgroundColor: '#2563eb',
                          color: 'white',
                          borderRadius: '0.5rem',
                          border: 'none',
                          fontSize: '0.875rem',
                          fontWeight: '500',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#1d4ed8'}
                        onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
                      >
                        Accept All
                      </button>
                      <button
                        onClick={handleAcceptNecessary}
                        style={{
                          padding: '0.75rem 1.5rem',
                          backgroundColor: '#e5e7eb',
                          color: '#374151',
                          borderRadius: '0.5rem',
                          border: 'none',
                          fontSize: '0.875rem',
                          fontWeight: '500',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#d1d5db'}
                        onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#e5e7eb'}
                      >
                        Necessary Only
                      </button>
                      <button
                        onClick={() => setShowDetails(!showDetails)}
                        style={{
                          padding: '0.75rem 1.5rem',
                          backgroundColor: 'transparent',
                          color: '#2563eb',
                          borderRadius: '0.5rem',
                          border: '1px solid #2563eb',
                          fontSize: '0.875rem',
                          fontWeight: '500',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#eff6ff'}
                        onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        {showDetails ? 'Hide Details' : 'Customize'}
                      </button>
                    </div>

                    {/* Detailed Preferences */}
                    <AnimatePresence>
                      {showDetails && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          style={{ borderTop: '1px solid #e5e7eb', paddingTop: '1rem' }}
                        >
                          <h4 style={{ fontWeight: '500', color: '#111827', marginBottom: '0.75rem' }}>Cookie Preferences</h4>
                          
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                            {/* Necessary Cookies */}
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem', backgroundColor: '#f9fafb', borderRadius: '0.5rem' }}>
                              <div style={{ flex: 1 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                  <input
                                    type="checkbox"
                                    checked={preferences.necessary}
                                    disabled
                                    style={{ width: '1rem', height: '1rem' }}
                                  />
                                  <span style={{ fontWeight: '500', color: '#111827' }}>Necessary Cookies</span>
                                  <span style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem', backgroundColor: '#e5e7eb', color: '#374151', borderRadius: '0.25rem' }}>Always Active</span>
                                </div>
                                <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.25rem', marginLeft: '1.5rem' }}>
                                  Essential for the website to function properly. Cannot be disabled.
                                </p>
                              </div>
                            </div>

                            {/* Analytics Cookies */}
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem', backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '0.5rem' }}>
                              <div style={{ flex: 1 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                  <input
                                    type="checkbox"
                                    checked={preferences.analytics}
                                    onChange={() => handleToggle('analytics')}
                                    style={{ width: '1rem', height: '1rem' }}
                                  />
                                  <span style={{ fontWeight: '500', color: '#111827' }}>Analytics Cookies</span>
                                </div>
                                <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.25rem', marginLeft: '1.5rem' }}>
                                  Help us understand how visitors interact with our website by collecting and reporting information anonymously.
                                </p>
                              </div>
                            </div>

                            {/* Advertising Cookies */}
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem', backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '0.5rem' }}>
                              <div style={{ flex: 1 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                  <input
                                    type="checkbox"
                                    checked={preferences.ads}
                                    onChange={() => handleToggle('ads')}
                                    style={{ width: '1rem', height: '1rem' }}
                                  />
                                  <span style={{ fontWeight: '500', color: '#111827' }}>Advertising Cookies</span>
                                </div>
                                <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.25rem', marginLeft: '1.5rem' }}>
                                  Used to make advertising messages more relevant to you and your interests.
                                </p>
                              </div>
                            </div>

                            {/* Personalization Cookies */}
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem', backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '0.5rem' }}>
                              <div style={{ flex: 1 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                  <input
                                    type="checkbox"
                                    checked={preferences.personalization}
                                    onChange={() => handleToggle('personalization')}
                                    style={{ width: '1rem', height: '1rem' }}
                                  />
                                  <span style={{ fontWeight: '500', color: '#111827' }}>Personalization Cookies</span>
                                </div>
                                <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.25rem', marginLeft: '1.5rem' }}>
                                  Allow the website to remember choices you make and provide enhanced, more personal features.
                                </p>
                              </div>
                            </div>
                          </div>

                          <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'flex-end' }}>
                            <button
                              onClick={handleSavePreferences}
                              style={{
                                padding: '0.5rem 1.5rem',
                                backgroundColor: '#2563eb',
                                color: 'white',
                                borderRadius: '0.5rem',
                                border: 'none',
                                fontSize: '0.875rem',
                                fontWeight: '500',
                                cursor: 'pointer',
                                transition: 'background-color 0.2s'
                              }}
                              onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#1d4ed8'}
                              onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
                            >
                              Save Preferences
                            </button>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    {/* Privacy Links */}
                    <div style={{ fontSize: '0.75rem', color: '#6b7280', textAlign: 'center', borderTop: '1px solid #e5e7eb', paddingTop: '1rem' }}>
                      By using our site, you agree to our{' '}
                      <button
                        onClick={() => setShowPrivacyModal(true)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#2563eb',
                          textDecoration: 'underline',
                          cursor: 'pointer',
                          fontSize: '0.75rem',
                          padding: 0,
                          fontFamily: 'inherit'
                        }}
                      >
                        Privacy Policy
                      </button>{' '}
                      and{' '}
                      <button
                        onClick={() => setShowTermsModal(true)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#2563eb',
                          textDecoration: 'underline',
                          cursor: 'pointer',
                          fontSize: '0.75rem',
                          padding: 0,
                          fontFamily: 'inherit'
                        }}
                      >
                        Terms of Service
                      </button>
                      .
                    </div>
                  </div>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>,
        document.body
      )}

      {/* Privacy Policy Modal */}
      <PrivacyPolicyModal
        isVisible={showPrivacyModal}
        onClose={() => setShowPrivacyModal(false)}
      />

      {/* Terms of Service Modal */}
      <TermsOfServiceModal
        isVisible={showTermsModal}
        onClose={() => setShowTermsModal(false)}
      />
    </>
  );
};

export default ConsentBanner; 